
import { EasyEvent, isCanvasEvent } from "@/util/easy-event";
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import Icon from "@/components/UI/Icon.vue";
import { Breakpoint } from "@/types/breakpoints";
import { CanvasPreviewWidth } from "@/types/ui";

@Component({
  components: { Icon }
})
export default class PreviewWidthAdjustmentHandle extends Vue {
  @Prop()
  iframeWrapperPosition!: {
    x: number;
    y: number;
    width: number;
    height: number;
  };

  @Prop() previewWidth!: CanvasPreviewWidth;
  @Prop() currentBreakpoint!: Breakpoint;

  @Prop() mainWrapper!: HTMLDivElement | null;
  @Prop() iframe!: HTMLIFrameElement | null;

  get currentPreviewWidthIsSetByUser() {
    if (this.currentBreakpoint.defaultPreviewWidth === "100%") {
      return this.previewWidth.width !== "100%";
    } else {
      return (
        this.previewWidth.width !== this.currentBreakpoint.defaultPreviewWidth
      );
    }
  }

  get style() {
    return {
      left: `${
        this.iframeWrapperPosition.x + this.iframeWrapperPosition.width
      }px`,
      height: `${this.iframeWrapperPosition.height}px`
    };
  }

  get pxAmount() {
    return this.previewWidth.width === "100%"
      ? "100%"
      : `${this.previewWidth.width}px`;
  }

  get alreadyAtMaximumWidth() {
    if (this.previewWidth.width === "100%") return true;

    return (
      this.previewWidth.width >= this.previewWidth.availableCanvasSize.width
    );
  }

  mouseXChange = 0;
  lastMouseX = 0;

  isDragging = false;

  easyEvent = new EasyEvent();

  dragStart(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    this.easyEvent.on("mousemove", this.dragging);
    this.easyEvent.on("mouseup", this.dragEnd);

    this.lastMouseX = e.clientX;

    if (this.iframe) {
      // without this, once the mouse goes over the iframe, the mousemove event stops firing
      this.iframe.style.pointerEvents = "none";
    }

    if (this.mainWrapper) {
      this.mainWrapper.style.cursor = "col-resize";
    }
  }

  dragging(e: MouseEvent) {
    if (!isCanvasEvent(e)) {
      this.isDragging = true;

      e.preventDefault();
      e.stopPropagation();

      this.mouseXChange = e.clientX - this.lastMouseX;

      this.adjustPreviewWidth(this.mouseXChange * 2);

      this.lastMouseX = e.clientX;
    }
  }

  dragEnd(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    this.easyEvent.removeAll();

    if (this.iframe) {
      this.iframe.style.pointerEvents = "auto";
    }

    if (this.mainWrapper) {
      this.mainWrapper.style.cursor = "auto";
    }

    this.isDragging = false;
  }

  adjustPreviewWidth(adjustX: number) {
    let newPreviewWidth: number;

    const previousPreviewWidth = this.previewWidth.width;

    if (this.alreadyAtMaximumWidth && adjustX > 0) {
      adjustX = 0;
    }

    if (this.previewWidth.width === "100%") {
      if (!this.iframe) {
        console.warn("iframe element is missing");
        return;
      }
      newPreviewWidth = this.iframe.offsetWidth - adjustX;
    } else {
      newPreviewWidth = this.previewWidth.width + adjustX;
    }

    if (
      previousPreviewWidth !== "100%" &&
      newPreviewWidth > previousPreviewWidth &&
      newPreviewWidth > this.previewWidth.availableCanvasSize.width
    ) {
      newPreviewWidth = this.previewWidth.availableCanvasSize.width;
    }

    this.$emit("previewWidthChangeWithAdjustmentBar", newPreviewWidth);
  }

  resetPreviewWidth() {
    this.$emit("resetPreviewWidth");
  }

  get classes() {
    return {
      "breakdance-preview-width-bar": true,
      "breakdance-preview-width-bar-is-dragging": this.isDragging
    };
  }
}
